import { useState, useRef, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import testimonials from "../data/testimonials.json";
import "flag-icons/css/flag-icons.min.css";


export default function Testimonials() {
  const [activeTab, setActiveTab] = useState("asistentes");
  const swiperRef = useRef(null);
  const containerRef = useRef(null);
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  const filteredTestimonials = testimonials.filter((t) => t.type === activeTab);

  const updateNavPosition = () => {
    const container = containerRef.current;
    const prevBtn = prevRef.current;
    const nextBtn = nextRef.current;
    const swiper = swiperRef.current;
    if (!container || !prevBtn || !nextBtn || !swiper) return;

    const activeSlide = container.querySelector(".swiper-slide-active");
    if (!activeSlide) return;

    const contRect = container.getBoundingClientRect();
    const slideRect = activeSlide.getBoundingClientRect();

    const btnWidth = prevBtn.offsetWidth;
    const gap = 12;

    const leftPos = slideRect.left - contRect.left;
    const rightPos = slideRect.right - contRect.left;

    let prevLeft = leftPos - btnWidth - gap;
    if (prevLeft < 6) prevLeft = 6;

    let nextLeft = rightPos + gap;
    if (nextLeft + btnWidth > contRect.width - 6) {
      nextLeft = contRect.width - btnWidth - 6;
    }

    prevBtn.style.left = `${prevLeft}px`;
    nextBtn.style.left = `${nextLeft}px`;

    const slideCenterY = slideRect.top + slideRect.height / 2 - contRect.top;
    prevBtn.style.top = `calc(${slideCenterY}px - ${btnWidth / 2}px)`;
    nextBtn.style.top = `calc(${slideCenterY}px - ${btnWidth / 2}px)`;
  };

  useEffect(() => {
    const id = setTimeout(updateNavPosition, 60);
    return () => clearTimeout(id);
  }, [activeTab]);

  useEffect(() => {
    const onResize = () => {
      updateNavPosition();
    };
    window.addEventListener("resize", onResize);

    const swiper = swiperRef.current;
    if (swiper) {
      swiper.on("slideChange", updateNavPosition);
      swiper.on("transitionEnd", updateNavPosition);
      swiper.on("resize", updateNavPosition);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      if (swiper) {
        swiper.off("slideChange", updateNavPosition);
        swiper.off("transitionEnd", updateNavPosition);
        swiper.off("resize", updateNavPosition);
      }
    };
  }, []);

  const slidesPerViewDefault = 2;
  const isSingleSlide = filteredTestimonials.length === 1;
  const enableLoop = filteredTestimonials.length > slidesPerViewDefault;

  return (
    <section className={`testimonials ${activeTab === "ponentes" ? "blue-bg" : ""}`}>
      <h2 className={`${activeTab === "ponentes" ? "green" : "black"}`}>Testimonios Reales</h2>
      <p className={`subtitle ${activeTab === "ponentes" ? "white-text" : ""}`}>
        Voces que movilizan. Historias reales de mujeres que decidieron dar un
        paso y descubrieron un antes y un después. Porque cuando una se atreve,
        todas avanzamos.
      </p>

      <div className="testimonials-tabs">
        <button
          className={`tab ${activeTab === "asistentes" ? "active" : ""}`}
          onClick={() => setActiveTab("asistentes")}
        >
          Asistentes
        </button>
        <button
          className={`tab ${activeTab === "ponentes" ? "active green" : ""}`}
          onClick={() => setActiveTab("ponentes")}
        >
          Ponentes
        </button>
      </div>

      <div
        key={activeTab}
        className={`fade-container ${activeTab === "ponentes" ? "white" : ""}`}
      >
        <div ref={containerRef} className={`testimonials-carousel ${activeTab === "ponentes" ? "white" : ""}`}>
          <button
            ref={prevRef}
            className={`custom-nav custom-prev ${activeTab === "asistentes" ? "white" : ""}`}
            onClick={() => swiperRef.current && swiperRef.current.slidePrev()}
            aria-label="Anterior"
          >
            <svg width="18" height="30" viewBox="0 0 18 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 3L3 15L15 27" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </button>

          <button
            ref={nextRef}
            className={`custom-nav custom-next ${activeTab === "asistentes" ? "white" : ""}`}
            onClick={() => swiperRef.current && swiperRef.current.slideNext()}
            aria-label="Siguiente"
          >
            <svg width="18" height="30" viewBox="0 0 18 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 3L15 15L3 27" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            </svg>
          </button>

          <Swiper
            onSwiper={(sw) => {
              swiperRef.current = sw;
              setTimeout(updateNavPosition, 80);
            }}
            modules={[Pagination, Autoplay]}
            loop={enableLoop}
            grabCursor={!isSingleSlide}
            centeredSlides={!isSingleSlide ? true : false}
            slidesPerView={slidesPerViewDefault}
            spaceBetween={40}
            autoplay={isSingleSlide ? false : { delay: 2000, disableOnInteraction: false }}
            breakpoints={{
              0: { slidesPerView: 1, centeredSlides: true },
              480: { slidesPerView: 2, centeredSlides: true },
              768: { slidesPerView: 2 , centeredSlides: true },
              1024: { slidesPerView: 2, centeredSlides: true },
            }}
            className="swiper-testimonials"
          >
            {filteredTestimonials.map((item, idx) => (
              <SwiperSlide key={idx} className="speaker-slide">
                <div className="testimonial-card">
                  <div className="testimonial-img">
                    <img src={item.image} alt={item.name} />
                  </div>
                  {item.flag && (
                    <span
                      className={`flag-icon fi fi-${item.flag} fis absolute left-1/2 transform -translate-x-1/2 bottom-0 w-6 h-4`}
                    ></span>
                  )}

                  <h3 className={`testimonial-name ${activeTab === "ponentes" ? "blue-bg-text" : ""}`}>{item.name}</h3>
                  <p className={`testimonial-role ${activeTab === "ponentes" ? "blue-role" : ""}`}>{item.role}</p>
                  <blockquote className={`testimonial-text ${activeTab === "ponentes" ? "blue-bg-text-min" : ""}`}>
                    “{item.text}”
                  </blockquote>
                </div>

              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
